export interface BinaryOptions {
  /** Copy bytes into independent storage instead of sharing the input buffer. */
  copy?: boolean
}

export interface BinaryAPI {
  toUint8Array(input: ArrayBuffer | ArrayBufferView, options?: BinaryOptions): Uint8Array
  readBytes(input: ArrayBuffer | ArrayBufferView | Blob, options?: BinaryOptions): Promise<Uint8Array>
}

const bufferByteLength = Object.getOwnPropertyDescriptor(ArrayBuffer.prototype, 'byteLength')!.get!

function toUint8Array(input: ArrayBuffer | ArrayBufferView, { copy = false }: BinaryOptions = {}): Uint8Array {
  const view = ArrayBuffer.isView(input)
  let buffer: ArrayBuffer
  try {
    buffer = view ? input.buffer as ArrayBuffer : input as ArrayBuffer
    // The intrinsic getter checks the internal brand, including across realms.
    // Reject arrays, strings, spoofed objects, and SharedArrayBuffer explicitly.
    bufferByteLength.call(buffer)
  } catch {
    throw new TypeError('Aeppic.Binary: expected an ArrayBuffer or an ArrayBuffer-backed view')
  }
  let bytes: Uint8Array
  try {
    bytes = view
      ? new Uint8Array(buffer, input.byteOffset, input.byteLength)
      : new Uint8Array(buffer)
  } catch {
    throw new TypeError('Aeppic.Binary: cannot read a detached or invalid buffer')
  }
  return copy ? new Uint8Array(bytes) : bytes
}

async function readBytes(input: ArrayBuffer | ArrayBufferView | Blob, options?: BinaryOptions): Promise<Uint8Array> {
  const tag = Object.prototype.toString.call(input)
  if ((tag === '[object Blob]' || tag === '[object File]') && typeof (input as Blob).arrayBuffer === 'function') {
    return toUint8Array(await (input as Blob).arrayBuffer(), options)
  }
  return toUint8Array(input as ArrayBuffer | ArrayBufferView, options)
}

const binaryAPI: BinaryAPI = Object.freeze({ toUint8Array, readBytes })

/** Shared, stateless binary normalization. Does not fetch, upload, or encode data. */
export function createBinaryAPI(): BinaryAPI {
  return binaryAPI
}
